// The part merger. Given a list of {@link Part}s it bakes them into a single
// non-indexed BufferGeometry whose vertex colours already carry flat per-face
// shading from a fixed key light. The merged mesh therefore renders correctly
// under an *unlit* material (MeshBasicMaterial, vertexColors) — one cheap draw
// call, faceted low-poly look, no scene lights required, which is exactly how
// the viewer renders it. A lit material would need vertex normals this geometry
// deliberately omits (it would render black), so don't reach for one; the model
// still casts a shadow onto a lit pedestal regardless. Ported from age-of-mcp's
// `models/lowpoly.ts`.

import * as THREE from "three";
import type { Part } from "./types";

// Key light direction — from the upper-left, matching the viewer's sun so the
// baked shading and any real lighting agree.
const LIGHT = new THREE.Vector3(-0.45, 1.0, -0.35).normalize();
const SHADE_MIN = 0.58;
const SHADE_MAX = 1.2;

/** A face's brightness from how square-on it faces the key light. */
function faceShade(nx: number, ny: number, nz: number): number {
  const dot = nx * LIGHT.x + ny * LIGHT.y + nz * LIGHT.z; // -1..1
  const s = 0.85 + dot * 0.32;
  return s < SHADE_MIN ? SHADE_MIN : s > SHADE_MAX ? SHADE_MAX : s;
}

/** Compose a part's transform into a Matrix4 (position, Euler rotation, scale). */
export function partMatrix(part: Part): THREE.Matrix4 {
  const [px, py, pz] = part.position ?? [0, 0, 0];
  const [rx, ry, rz] = part.rotation ?? [0, 0, 0];
  const s = part.scale ?? 1;
  const [sx, sy, sz] = typeof s === "number" ? [s, s, s] : s;
  const euler = new THREE.Euler(rx, ry, rz, (part.eulerOrder ?? "XYZ") as THREE.EulerOrder);
  const quat = new THREE.Quaternion().setFromEuler(euler);
  return new THREE.Matrix4().compose(
    new THREE.Vector3(px, py, pz),
    quat,
    new THREE.Vector3(sx, sy, sz),
  );
}

/**
 * Merge `parts` into one flat-shaded, vertex-coloured BufferGeometry. Every
 * triangle gets a hard face normal and a shade from the key light, so the
 * result reads as faceted low-poly geometry. `offset` shifts every vertex (used
 * by `instantiateModel` to re-centre an animated node's parts on its pivot).
 */
export function buildModel(parts: Part[], offset: Vec3Like = [0, 0, 0]): THREE.BufferGeometry {
  const positions: number[] = [];
  const colors: number[] = [];
  const [ox, oy, oz] = offset;

  for (const part of parts) {
    const matrix = partMatrix(part);
    const cloned = part.geometry.clone().applyMatrix4(matrix);
    // toNonIndexed() warns if already non-indexed — only convert indexed ones.
    const g = cloned.index ? cloned.toNonIndexed() : cloned;
    const pos = g.getAttribute("position");
    const col = new THREE.Color(part.color);

    for (let i = 0; i < pos.count; i += 3) {
      const ax = pos.getX(i) + ox, ay = pos.getY(i) + oy, az = pos.getZ(i) + oz;
      const bx = pos.getX(i + 1) + ox, by = pos.getY(i + 1) + oy, bz = pos.getZ(i + 1) + oz;
      const cx = pos.getX(i + 2) + ox, cy = pos.getY(i + 2) + oy, cz = pos.getZ(i + 2) + oz;
      let nx = (by - ay) * (cz - az) - (bz - az) * (cy - ay);
      let ny = (bz - az) * (cx - ax) - (bx - ax) * (cz - az);
      let nz = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
      const len = Math.hypot(nx, ny, nz) || 1;
      const sh = faceShade(nx / len, ny / len, nz / len);
      const r = col.r * sh, gg = col.g * sh, bb = col.b * sh;
      positions.push(ax, ay, az, bx, by, bz, cx, cy, cz);
      colors.push(r, gg, bb, r, gg, bb, r, gg, bb);
    }
    g.dispose();
    if (g !== cloned) cloned.dispose();
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

type Vec3Like = readonly [number, number, number];
